import React from 'react';

/**
 * Pure label pagination. Skip applies to the first sheet only, and every label is placed:
 * the output has exactly ceil((skip + n) / perPage) sheets (invariant 5 in
 * docs/PLAN-REACT-SELLER-LABEL-PRINTER.md).
 *
 * @param {{labels: {key: string, text: string}[], skip: number, perPage: number}} args
 * @returns {Array<Array<{type: 'skipped'|'empty'} | {type: 'label', key: string, text: string}>>}
 */
export function paginateLabels({labels, skip = 0, perPage}) {
  const slotsPerSheet = Math.max(1, Math.floor(perPage) || 1);
  if (!labels.length) return [];
  const leading = Math.min(Math.max(0, Math.floor(skip) || 0), slotsPerSheet - 1);
  const sheets = [];
  const total = sheetCount({count: labels.length, skip: leading, perPage: slotsPerSheet});
  for (let sheet = 0; sheet < total; sheet += 1) {
    const slots = [];
    for (let slot = 0; slot < slotsPerSheet; slot += 1) {
      const index = sheet * slotsPerSheet + slot - leading;
      if (index < 0) {
        slots.push({type: 'skipped'});
      } else if (index < labels.length) {
        slots.push({type: 'label', key: labels[index].key, text: labels[index].text});
      } else {
        slots.push({type: 'empty'});
      }
    }
    sheets.push(slots);
  }
  return sheets;
}

/** Number of sheets needed for `count` labels with `skip` slots left blank on sheet 1. */
export function sheetCount({count, skip = 0, perPage}) {
  if (!count) return 0;
  const slotsPerSheet = Math.max(1, Math.floor(perPage) || 1);
  const leading = Math.min(Math.max(0, Math.floor(skip) || 0), slotsPerSheet - 1);
  return Math.ceil((leading + count) / slotsPerSheet);
}

/** Collapse blank lines and trim each line, like the classic print view did. */
export function normalizeLabelText(text) {
  return String(text || '')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .join('\n');
}

function readEditedText(element) {
  return element.innerText ?? element.textContent ?? '';
}

/**
 * Renders paginated labels as inch-sized sheets. Pure layout: selection, edits, and printing
 * belong to the caller. With `editable`, labels are contentEditable and report text on blur.
 */
export default function LabelSheets({
  labels,
  template,
  skip = 0,
  editable = false,
  onEdit,
}) {
  const sheets = paginateLabels({labels, skip, perPage: template.perPage});

  return (
    <div className="label-sheets">
      {sheets.map((slots, sheetIndex) => (
        <div
          key={sheetIndex}
          className="label-sheet"
          style={{width: `${template.pWidth}in`, height: `${template.pHeight}in`}}
        >
          {slots.map((slot, slotIndex) => {
            const style = {width: `${template.lWidth}in`, height: `${template.lHeight}in`};
            if (slot.type === 'skipped') {
              return (
                <div
                  key={slotIndex}
                  className="label-sheet__label label-sheet__label--skipped"
                  style={style}
                />
              );
            }
            if (slot.type === 'empty') {
              return (
                <div
                  key={slotIndex}
                  className="label-sheet__label label-sheet__label--empty"
                  style={style}
                />
              );
            }
            return (
              <div
                key={slot.key}
                className="label-sheet__label"
                style={style}
                {...(editable ? {
                  contentEditable: true,
                  suppressContentEditableWarning: true,
                  onBlur: event => onEdit && onEdit(slot.key, readEditedText(event.currentTarget)),
                } : {})}
              >
                {slot.text}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
